import { useCallback, useEffect, useRef, useState } from 'react';
import { listAnalyses } from 'api/historyApi';
import { ApiError } from 'api/restClient';
import type {
  AnalysisAuditSummary,
  AnalysisHistoryFilters,
  HistorySortKey,
  SortDir,
} from 'types/history';
import type { BacktestRuleSet } from 'hooks/useBacktest';

type Status = 'idle' | 'loading' | 'success' | 'error';

export const HISTORY_PAGE_SIZE = 50;

export interface UseAnalysisHistoryResult {
  rows: AnalysisAuditSummary[];
  total: number;
  status: Status;
  error: string | null;
  filters: AnalysisHistoryFilters;
  setFilters: (next: AnalysisHistoryFilters) => void;
  refresh: () => void;
  offset: number;
  setOffset: (next: number) => void;
  pageSize: number;
  /** null = the server's default (newest first). */
  sortKey: HistorySortKey | null;
  sortDir: SortDir;
  /** New column → descending; same column again → flip direction. */
  sortBy: (key: string) => void;
}

/**
 * Loads the analysis audit list, re-fetching whenever the filters, sort, page or
 * rule set change. Aborts any in-flight request on change/unmount so stale results never
 * land.
 *
 * Sorting is deliberately server-side: the client only ever holds one page, so
 * sorting locally would reorder 50 rows and call it "sorted history".
 */
export function useAnalysisHistory(pair?: BacktestRuleSet): UseAnalysisHistoryResult {
  const [rows, setRows] = useState<AnalysisAuditSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<AnalysisHistoryFilters>({});
  const [offset, setOffset] = useState(0);
  const [sortKey, setSortKey] = useState<HistorySortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Changing a filter always returns to the first page.
  const setFilters = useCallback((next: AnalysisHistoryFilters) => {
    setOffset(0);
    setFiltersState(next);
  }, []);

  // Re-sorting does too: page 2 of the old order has nothing to do with page 2
  // of the new one.
  const sortBy = useCallback((key: string) => {
    setOffset(0);
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir('desc');
      return key as HistorySortKey;
    });
  }, []);

  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const load = useCallback(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus('loading');
    setError(null);

    listAnalyses(
      {
        ...filters,
        limit: HISTORY_PAGE_SIZE,
        offset,
        sortBy: sortKey,
        sortDir,
        // Verdicts and grades are stored per rule set, so either filter is only
        // meaningful alongside the rule set the table is currently showing.
        window: pair?.window,
        closeWindow: pair?.closeWindow,
        stopMult: pair?.stopMult,
        replayVersion: pair?.replayVersion,
      },
      ctrl.signal,
    )
      .then((res) => {
        if (!mountedRef.current || ctrl.signal.aborted) return;
        setRows(res.items);
        setTotal(res.total);
        setStatus('success');
      })
      .catch((e: unknown) => {
        if (!mountedRef.current || ctrl.signal.aborted) return;
        const msg =
          e instanceof ApiError
            ? e.status === 403
              ? 'Your account is not permitted to view analysis history (admin only).'
              : `${e.message} (${e.status})`
            : e instanceof Error
              ? e.message
              : 'Failed to load history';
        setError(msg);
        setStatus('error');
      });
  }, [filters, offset, sortKey, sortDir, pair?.window, pair?.closeWindow, pair?.stopMult, pair?.replayVersion]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    rows,
    total,
    status,
    error,
    filters,
    setFilters,
    refresh: load,
    offset,
    setOffset,
    pageSize: HISTORY_PAGE_SIZE,
    sortKey,
    sortDir,
    sortBy,
  };
}
